<template>
  <div>
    <header
      class="fixed left-0 right-0 h-14 bg-brand-600 z-20 lg:hidden flex items-center px-4"
      :style="{ top: offsetTop }"
    >
      <!-- Hamburger -->
      <button
        @click="toggleSidebar"
        class="text-white p-2 hover:bg-brand-700 rounded-md transition-colors -ml-2"
        aria-label="Toggle navigation"
      >
        <svg
          v-if="!isSidebarOpen"
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>

        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <!-- LOGO here -->

      <h1 class="text-white text-lg font-semibold ml-3">IDH Salary Matrix</h1>
    </header>

    <!-- Overlay (visible on tablet when sidebar is open) -->
    <div
      v-if="isSidebarOpen"
      @click="closeSidebar"
      class="fixed top-14 left-0 right-0 bottom-0 bg-black bg-opacity-50 z-20 md:hidden transition-opacity"
    ></div>

    <!-- Sidebar Navigation -->
    <nav
      id="sidebar-nav"
      :class="[
        'sidebar-nav',
        {
          '-translate-x-full lg:translate-x-0': !isSidebarOpen,
          'translate-x-0': isSidebarOpen,
        },
      ]"
    >
      <!-- Logo Section (only visible on desktop) -->
      <div class="px-5 py-6 hidden lg:block lg:relative">
        <h1 class="text-xl font-bold text-white flex items-center gap-x-2">
          <span>IDH </span>
          <div class="flex-1 min-w-0">
            <div class="truncate">Salary Matrix</div>
          </div>
        </h1>
      </div>

      <!-- Main Navigation Links -->
      <div class="flex-1 px-4 space-y-1 pt-4 lg:pt-0">
        <a
          :href="dashboardPath"
          :class="[
            'nav-link',
            isDashboardActive
              ? 'text-white bg-brand-700'
              : 'text-white hover:bg-brand-25 hover:bg-opacity-10',
          ]"
          style="text-decoration: none"
          @click="handleLinkClick"
        >
          <DashboardIcon class="flex-none" />
          <div class="flex-1 min-w-0">
            <div class="truncate">
              <Translation name="navigation.dashboard" default="Dashboard" />
            </div>
          </div>
        </a>

        <a
          :href="facilitiesPath"
          :class="[
            'nav-link',
            isFacilitiesActive
              ? 'text-white bg-brand-700'
              : 'text-white hover:bg-brand-25 hover:bg-opacity-10',
          ]"
          style="text-decoration: none"
          data-tour-step="facilities"
          @click="handleLinkClick"
        >
          <FacilityNavIcon class="flex-none" />
          <div class="flex-1 min-w-0">
            <div class="truncate">
              <Translation name="navigation.facilities" default="Facilities" />
            </div>
          </div>
        </a>

        <a
          :href="wageCalculationsPath"
          :class="[
            'nav-link',
            isWageCalculationsActive
              ? 'text-white bg-brand-700'
              : 'text-white hover:bg-brand-25 hover:bg-opacity-10',
          ]"
          style="text-decoration: none"
          data-tour-step="wage-calculations"
          @click="handleLinkClick"
        >
          <CalculationIcon class="flex-none" />
          <div class="flex-1 min-w-0">
            <div class="truncate">
              <Translation
                name="navigation.wage_calculations"
                default="Wage Calculations"
              />
            </div>
          </div>
        </a>

        <a
          v-if="showCollaborationsNav"
          :href="collaborationsPath"
          :class="[
            'nav-link',
            isCollaborationsActive
              ? 'text-white bg-brand-700'
              : 'text-white hover:bg-brand-25 hover:bg-opacity-10',
          ]"
          style="text-decoration: none"
          @click="handleLinkClick"
        >
          <CollaborationNavIcon class="flex-none" />
          <div class="flex-1 min-w-0">
            <div class="truncate">
              <Translation
                name="navigation.collaborations"
                default="Collaborations"
              />
            </div>
          </div>
          <span
            v-if="collaborationsNotificationCount > 0"
            class="announcement-badge ml-auto bg-white text-brand-800 text-xs rounded-full px-2 py-1"
          >
            {{ collaborationsNotificationCount }}
          </span>
        </a>
      </div>

      <div class="px-4 py-6 space-y-1">
        <a
          :href="announcementsPath"
          :class="[
            'nav-link',
            isAnnouncementsActive
              ? 'text-white bg-brand-700'
              : 'text-white hover:bg-brand-25 hover:bg-opacity-10',
          ]"
          style="text-decoration: none"
          data-turbo="false"
          data-tour-step="announcements"
          @click="handleLinkClick"
        >
          <AnnouncementIcon class="flex-none" />
          <div class="flex-1 min-w-0">
            <div class="truncate">
              <Translation
                name="navigation.announcements"
                default="Announcements"
              />
            </div>
          </div>
          <span
            v-if="displayUnviewedCount > 0"
            class="announcement-badge ml-auto bg-white text-brand-800 text-xs rounded-full px-2 py-1"
          >
            {{ displayUnviewedCount }}
          </span>
        </a>

        <!-- Guided Tour - Only visible on Dashboard -->
        <template v-if="isDashboardActive">
          <a
            href="#"
            class="text-white hover:bg-brand-25 hover:bg-opacity-10 nav-link"
            style="text-decoration: none"
            data-tour-step="guided-tour"
            @click.prevent="openGuidedTourModal"
          >
            <GuidedTourIcon class="flex-none" />
            <div class="flex-1 min-w-0">
              <div class="truncate">
                <Translation
                  name="navigation.guided_tour"
                  default="Guided Tour"
                />
              </div>
            </div>
          </a>

          <!-- Guided Tour Modal -->
          <GuidedTourModal ref="guidedTourModalRef" />

          <!-- Guided Tour Tooltip -->
          <GuidedTourTooltip
            v-if="currentStepData"
            :is-visible="isTourActive"
            :title="currentStepData.title"
            :title-key="currentStepData.titleKey"
            :description="currentStepData.description"
            :description-key="currentStepData.descriptionKey"
            :current-step="currentTourStep"
            :total-steps="totalTourSteps"
            :target-element="currentTargetElement"
            @close="closeTour"
            @prev="prevTourStep"
            @next="nextTourStep"
          />
        </template>

        <a
          :href="eLearningPath"
          target="_blank"
          class="text-white hover:bg-brand-25 hover:bg-opacity-10 nav-link"
          style="text-decoration: none"
          data-tour-step="e-learning"
          @click="handleLinkClick"
        >
          <LearningIcon class="flex-none" />
          <div class="flex-1 min-w-0">
            <div class="truncate">
              <Translation name="navigation.e_learning" default="E-Learning" />
            </div>
          </div>
        </a>

        <a
          href="mailto:livingwagematrix@idhtrade.org"
          class="text-white hover:bg-brand-25 hover:bg-opacity-10 nav-link"
          style="text-decoration: none"
        >
          <ChatIcon class="flex-none" />
          <div class="flex-1 min-w-0">
            <div class="truncate">
              <Translation
                name="navigation.support"
                default="Contact Support"
              />
            </div>
          </div>
        </a>

        <div class="relative !mt-6">
          <button
            data-tour-step="profile"
            type="button"
            @click="toggleUserMenu"
            class="prof-button py-3 w-full rounded-lg flex items-start text-left hover:bg-opacity-20 transition-colors"
            style="box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05)"
          >
            <div
              class="h-10 w-10 rounded-full bg-brand-400 flex items-center justify-center flex-none mr-2"
            >
              <span class="text-sm font-semibold text-brand-50">
                {{ userInitials }}
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-gray-50 font-semibold text-sm truncate">
                {{ userName }}
              </div>
              <div class="text-white text-opacity-70 text-sm truncate">
                {{ userEmail }}
              </div>
            </div>
            <DropdownArrowsIcon class="ml-auto hidden prof-toggle" />
          </button>
          <div
            v-if="isUserMenuOpen"
            class="prof-menu absolute bottom-full left-0 mb-2 w-full rounded-md shadow-lg bg-white ring-1 ring-black/5"
          >
            <a
              :href="profilePath"
              :class="[
                'block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-md',
                isProfileActive ? 'font-semibold bg-gray-100' : '',
              ]"
            >
              <Translation
                name="navigation.profile_settings"
                default="Profile Settings"
              />
            </a>
            <a
              :href="logoutPath"
              data-turbo-method="delete"
              class="rounded-b-md block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <Translation name="navigation.logout" default="Logout" />
            </a>
          </div>
        </div>
      </div>
    </nav>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import DashboardIcon from "./icons/DashboardIcon.vue";
import FacilityNavIcon from "./icons/FacilityNavIcon.vue";
import CalculationIcon from "./icons/CalculationIcon.vue";
import CollaborationNavIcon from "./icons/CollaborationNavIcon.vue";
import AnnouncementIcon from "./icons/AnnouncementIcon.vue";
import ChatIcon from "./icons/ChatIcon.vue";
import GuidedTourIcon from "./icons/GuidedTourIcon.vue";
import DropdownArrowsIcon from "./icons/DropdownArrowsIcon.vue";
import LearningIcon from "./icons/LearningIcon.vue";
import GuidedTourModal from "./GuidedTourModal.vue";
import GuidedTourTooltip from "./GuidedTourTooltip.vue";
import Translation from "./aft/Translation.vue";

// Props
const props = defineProps({
  dashboardPath: String,
  facilitiesPath: String,
  wageCalculationsPath: String,
  collaborationsPath: {
    type: String,
    default: "#",
  },
  announcementsPath: String,
  eLearningPath: String,
  profilePath: String,
  logoutPath: String,
  currentPath: String,
  userName: String,
  userEmail: String,
  userInitials: String,
  offsetTop: {
    type: Number,
    default: 0,
  },
  unviewedCount: {
    type: Number,
    default: 0,
  },
  collaborationsNotificationCount: {
    type: Number,
    default: 0,
  },
  showCollaborationsNav: {
    type: Boolean,
    default: false,
  },
  guidedTourShown: {
    type: Boolean,
    default: false,
  },
});

// State
const isSidebarOpen = ref(false);
const isUserMenuOpen = ref(false);
const announcementsViewed = ref(false);
const guidedTourModalRef = ref(null);

// Tour state
const isTourActive = ref(false);
const currentTourStep = ref(1);
const currentTargetElement = ref(null);

// Tour steps configuration
const tourSteps = [
  {
    targetSelector: '[data-tour-step="facilities"]',
    title: "Facilities",
    titleKey: "guided_tour.step_facilities_title",
    description:
      "Tooltips are used to describe or identify an element. In most scenarios, tooltips help the user understand meaning, function or alt-text.",
    descriptionKey: "guided_tour.step_facilities_description",
  },
  {
    targetSelector: '[data-tour-step="wage-calculations"]',
    title: "Wage Calculations",
    titleKey: "guided_tour.step_wage_calculations_title",
    description:
      "Tooltips are used to describe or identify an element. In most scenarios, tooltips help the user understand meaning, function or alt-text.",
    descriptionKey: "guided_tour.step_wage_calculations_description",
  },
  {
    targetSelector: '[data-tour-step="profile"]',
    title: "Profile Settings",
    titleKey: "guided_tour.step_profile_title",
    description:
      "Tooltips are used to describe or identify an element. In most scenarios, tooltips help the user understand meaning, function or alt-text.",
    descriptionKey: "guided_tour.step_profile_description",
  },
  {
    targetSelector: '[data-tour-step="announcements"]',
    title: "Announcements",
    titleKey: "guided_tour.step_announcements_title",
    description:
      "Tooltips are used to describe or identify an element. In most scenarios, tooltips help the user understand meaning, function or alt-text.",
    descriptionKey: "guided_tour.step_announcements_description",
  },
  {
    targetSelector: '[data-tour-step="e-learning"]',
    title: "E-Learning",
    titleKey: "guided_tour.step_e_learning_title",
    description:
      "Tooltips are used to describe or identify an element. In most scenarios, tooltips help the user understand meaning, function or alt-text.",
    descriptionKey: "guided_tour.step_e_learning_description",
  },
];

function pathMatches(current, target) {
  if (!target || target === "#") return false;
  return (
    current === target ||
    (target.length > 1 && current.startsWith(target))
  );
}

// Computed
const displayUnviewedCount = computed(() => {
  // If announcements have been viewed, always show 0
  if (announcementsViewed.value) {
    return 0;
  }
  // Otherwise, show the server-provided count
  return props.unviewedCount;
});

const isDashboardActive = computed(
  () => props.currentPath === props.dashboardPath
);
const isFacilitiesActive = computed(
  () =>
    props.currentPath === props.facilitiesPath ||
    props.currentPath.startsWith(props.facilitiesPath)
);
const isWageCalculationsActive = computed(
  () =>
    props.currentPath === props.wageCalculationsPath ||
    props.currentPath.startsWith(props.wageCalculationsPath)
);
const isCollaborationsActive = computed(() =>
  pathMatches(props.currentPath, props.collaborationsPath)
);
const isAnnouncementsActive = computed(
  () => props.currentPath === props.announcementsPath
);
const isProfileActive = computed(() => props.currentPath === props.profilePath);

// Computed for tour
const currentStepData = computed(() => {
  return tourSteps[currentTourStep.value - 1] || null;
});

const totalTourSteps = computed(() => tourSteps.length);

// Methods
const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
  if (isSidebarOpen.value) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }
};

const closeSidebar = () => {
  isSidebarOpen.value = false;
  document.body.style.overflow = "";
};

const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
};

const handleLinkClick = () => {
  // Close sidebar on mobile/tablet when clicking a link
  if (window.innerWidth < 1024) {
    closeSidebar();
  }
};

const openGuidedTourModal = () => {
  // Close sidebar on mobile/tablet when opening modal
  if (window.innerWidth < 1024) {
    closeSidebar();
  }
  guidedTourModalRef.value?.openModal();
};

// Tour methods
const startTour = () => {
  // On mobile/tablet (< 1024px), open the sidebar overlay
  if (window.innerWidth < 1024) {
    isSidebarOpen.value = true;
    document.body.style.overflow = "hidden";
  }

  // On desktop, ensure sidebar is expanded
  const sidebar = document.getElementById("sidebar-nav");
  if (sidebar) {
    sidebar.classList.add("expanded");
  }

  // Wait for sidebar transition to complete before showing tooltip
  setTimeout(() => {
    isTourActive.value = true;
    currentTourStep.value = 1;
    // Find and set the target element for the first step
    updateTargetElement();
  }, 300); // Match sidebar transition duration
};

const updateTargetElement = () => {
  const stepData = tourSteps[currentTourStep.value - 1];
  if (stepData) {
    // Open the user menu if the profile step is active
    if (stepData.targetSelector === '[data-tour-step="profile"]') {
      isUserMenuOpen.value = true;
      // Wait for the menu to open before finding the element
      setTimeout(() => {
        currentTargetElement.value = document.querySelector(
          stepData.targetSelector
        );
      }, 50);
    } else {
      // Close the user menu for other steps
      isUserMenuOpen.value = false;
      currentTargetElement.value = document.querySelector(
        stepData.targetSelector
      );
    }
  }
};

const closeTour = () => {
  isTourActive.value = false;
  currentTourStep.value = 1;
  currentTargetElement.value = null;
  isUserMenuOpen.value = false;

  // On mobile/tablet (< 1024px), close the sidebar when tour ends
  if (window.innerWidth < 1024) {
    closeSidebar();
  }
};

const prevTourStep = () => {
  if (currentTourStep.value > 1) {
    currentTourStep.value--;
    updateTargetElement();
  }
};

const nextTourStep = () => {
  if (currentTourStep.value < tourSteps.length) {
    currentTourStep.value++;
    updateTargetElement();
  } else {
    // Tour finished
    closeTour();
  }
};

const handleTourStart = () => {
  startTour();
};

const handleResize = () => {
  // Auto-close sidebar on desktop
  if (window.innerWidth >= 1024) {
    closeSidebar();
  }
};

const handleClickOutside = (event) => {
  if (isUserMenuOpen.value && !event.target.closest(".prof-button")) {
    isUserMenuOpen.value = false;
  }
};

const handleEscape = (event) => {
  if (event.key === "Escape") {
    if (isSidebarOpen.value) {
      closeSidebar();
    }
    if (isUserMenuOpen.value) {
      isUserMenuOpen.value = false;
    }
  }
};

// Event handler for announcements viewed
const handleAnnouncementsViewed = () => {
  announcementsViewed.value = true;
};

// Mark guided tour as shown in the database
const markGuidedTourShown = async () => {
  try {
    const locale = window.location.pathname.split("/")[1] || "en";
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");

    if (!csrfToken) {
      console.error("CSRF token not found - cannot mark guided tour as shown");
      return;
    }

    const response = await fetch(`/${locale}/profile/guided_tour_shown`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "application/json",
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        "Failed to mark guided tour as shown:",
        response.status,
        errorData
      );
    }
  } catch (error) {
    console.error("Failed to mark guided tour as shown:", error);
  }
};

// Handle modal close event
const handleGuidedTourModalClose = () => {
  if (!props.guidedTourShown) {
    markGuidedTourShown();
  }
};

// Lifecycle
onMounted(() => {
  window.addEventListener("resize", handleResize);
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleEscape);
  window.addEventListener("announcements:viewed", handleAnnouncementsViewed);
  window.addEventListener("guided-tour:start", handleTourStart);
  window.addEventListener("guided-tour:closed", handleGuidedTourModalClose);

  //handle sidebar expanded state
  const sidebar = document.getElementById("sidebar-nav");
  sidebar.addEventListener("mouseenter", () => {
    if (!isTourActive.value) {
      sidebar.classList.add("expanded");
      localStorage.setItem("sidebarExpanded", "true");
    }
  });

  sidebar.addEventListener("mouseleave", () => {
    if (!isTourActive.value) {
      sidebar.classList.remove("expanded");
      localStorage.removeItem("sidebarExpanded");
      // Close the user menu dropdown when sidebar collapses
      isUserMenuOpen.value = false;
    }
  });

  //if menu item is clicked and new page loads
  if (localStorage.getItem("sidebarExpanded")) {
    sidebar.classList.add("expanded");
  }

  // Auto-open guided tour modal on first login
  if (!props.guidedTourShown) {
    setTimeout(() => {
      guidedTourModalRef.value?.openModal();
    }, 500);
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleEscape);
  window.removeEventListener("announcements:viewed", handleAnnouncementsViewed);
  window.removeEventListener("guided-tour:start", handleTourStart);
  window.removeEventListener("guided-tour:closed", handleGuidedTourModalClose);
  document.body.style.overflow = "";
});
</script>
